/**
 * EventSystem.ts — Simple typed event bus for decoupled game events.
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

export type EventHandler<T = unknown> = (data: T) => void;

export class EventSystem {
  private handlers = new Map<string, EventHandler[]>();

  /** Subscribe to an event */
  on<T = unknown>(event: string, handler: EventHandler<T>): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    this.handlers.get(event)!.push(handler as EventHandler);
  }

  /** Unsubscribe from an event */
  off<T = unknown>(event: string, handler: EventHandler<T>): void {
    const list = this.handlers.get(event);
    if (list) {
      const index = list.indexOf(handler as EventHandler);
      if (index >= 0) {
        list.splice(index, 1);
      }
    }
  }

  /** Emit an event to all subscribers */
  emit<T = unknown>(event: string, data?: T): void {
    const list = this.handlers.get(event);
    if (list) {
      for (const handler of [...list]) {
        try {
          handler(data);
        } catch (err) {
          console.error(`Event handler for "${event}" threw:`, err);
        }
      }
    }
  }

  /** Remove all handlers for an event (or all events if no name specified) */
  clear(event?: string): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }

  /** Check if an event has any subscribers */
  hasListeners(event: string): boolean {
    return (this.handlers.get(event)?.length ?? 0) > 0;
  }
}
